import { inject, Injectable, InjectionToken } from '@angular/core';
import { SharedAppSettingsService } from '@luggagehero/shared/app-settings/data-access';
import { SharedLoggingService } from '@luggagehero/shared/services/logging';
import { SharedStorageService, StorageTarget } from '@luggagehero/shared/services/storage';
import { TranslateLoader } from '@ngx-translate/core';
import { BehaviorSubject, catchError, forkJoin, map, Observable, of, switchMap, take, tap } from 'rxjs';

import { TranslationMap } from './+state';
import { EN_FALLBACK } from './en-fallback';
import { SharedTranslationsService } from './shared-translations.service';

/**
 * Last-resort English layer, generated from the live collection (never hand-edited —
 * `pnpm sync:en-fallback`). Covers the one case API + cache cannot: a cold install that
 * cannot reach /app_text at all (e.g. first native launch offline). Injectable so tests
 * can substitute a fixture.
 */
export const EN_FALLBACK_SEED = new InjectionToken<TranslationMap>('EN_FALLBACK_SEED', {
  providedIn: 'root',
  factory: () => EN_FALLBACK,
});

/**
 * Local-storage key prefix for the last successful /app_text response per language.
 * Storage is origin-scoped (and app-scoped on native), so the language is enough to key on.
 */
const APP_TEXT_CACHE_KEY_PREFIX = 'lhAppTextCache.';

@Injectable()
export class SharedTranslateLoader implements TranslateLoader {
  private translationsService = inject(SharedTranslationsService);
  private appSettingsService = inject(SharedAppSettingsService);
  private storage = inject(SharedStorageService);
  private log = inject(SharedLoggingService);
  private enSeed = inject(EN_FALLBACK_SEED);

  public static get loadedLanguage$(): Observable<string[]> {
    return SharedTranslateLoader.loadedLanguages.asObservable();
  }

  // BehaviorSubject (not EventEmitter/Subject): the load can complete synchronously when the
  // cache is warm and the API branch short-circuits, i.e. BEFORE MultilingualService.init()
  // subscribes to loadedLanguage$ — without replay that emission is lost and app bootstrap,
  // which awaits it, hangs forever.
  private static loadedLanguages = new BehaviorSubject<string[]>([]);
  private static languageSet = new Set<string>();

  public getTranslation(lang: string): Observable<TranslationMap> {
    // The API is the single source of truth; the last successful response is cached and
    // used as the base layer so a failed request degrades to slightly-stale copy instead
    // of raw SCREAMING_SNAKE_CASE keys (AM-4589/AM-4590). Merging (API wins) rather than
    // picking one source means a key missing from either side still resolves.
    //
    // Layering: committed English seed (en only) → cached last-good → live API, later
    // layers winning. The seed covers the one case the other two cannot — a cold install
    // that cannot reach /app_text at all — so English copy always exists; non-English
    // languages reach it per-key via ngx-translate's defaultLanguage 'en' fallback.
    // Residual worst case: a cold, offline NON-English load renders English (not raw keys)
    // until the API is reachable; recovery is on the next full page load.
    return forkJoin({
      seed: of(lang === 'en' ? this.enSeed : {}),
      cached: of(this.readCache(lang)),
      api: this.getTranslationFromApi(lang).pipe(
        tap((res) => this.writeCache(lang, res)),
        catchError((err: unknown) => {
          // This failure is the mechanism that produces raw keys — it must be visible in
          // Datadog (AM-4589 was unattributable because failures here were silent). The
          // Error goes as its own top-level argument: SharedLoggingService only maps a
          // direct Error instance to the Datadog error field; nested it would serialise
          // to {} and lose the stack.
          void this.log.error('Failed to load app text from API', { lang }, err);
          return of<TranslationMap>({});
        }),
      ),
    }).pipe(
      map(({ seed, cached, api }) => ({ ...seed, ...cached, ...api })),
      // Register unconditionally on completion: "loaded" means the load attempt finished,
      // not that keys exist. App bootstrap awaits this signal (multilingual.service.ts), so
      // gating it on a non-empty map would turn "API down on a cold start" into a blank,
      // never-bootstrapping app — strictly worse than raw keys.
      tap(() => SharedTranslateLoader.registerLoadedLanguage(lang)),
    );
  }

  private getTranslationFromApi(lang: string): Observable<TranslationMap> {
    return this.appSettingsService.$current.pipe(
      take(1),
      switchMap((appSettings) => {
        // Fail open: only an explicit false disables API translations. The flag has been
        // true for every app since 2025-03; treating "settings unavailable" as disabled
        // used to silently drop whole sessions onto the fallback (AM-4589).
        if (appSettings.isApiBasedAppTranslationsEnabled === false) {
          throw new Error('API based translations disabled');
        }
        return this.translationsService.getAppTranslations(lang);
      }),
    );
  }

  private readCache(lang: string): TranslationMap {
    try {
      const raw = this.storage.get(`${APP_TEXT_CACHE_KEY_PREFIX}${lang}`, StorageTarget.Local);
      return raw ? (JSON.parse(raw) as TranslationMap) : {};
    } catch {
      return {};
    }
  }

  private writeCache(lang: string, translations: TranslationMap): void {
    if (Object.keys(translations).length === 0) {
      // Belt on getAppTranslations' throw-on-empty contract: an empty map must never
      // overwrite a good cache.
      return;
    }
    try {
      // Store the API response verbatim (not merged with the previous cache) so keys
      // deleted server-side don't linger forever.
      this.storage.set(`${APP_TEXT_CACHE_KEY_PREFIX}${lang}`, JSON.stringify(translations), StorageTarget.Local);
    } catch {
      // Quota exceeded or storage unavailable — the cache is best-effort.
    }
  }

  private static registerLoadedLanguage(lang: string): void {
    if (SharedTranslateLoader.languageSet.has(lang)) {
      return;
    }
    SharedTranslateLoader.languageSet.add(lang);
    SharedTranslateLoader.loadedLanguages.next(Array.from(SharedTranslateLoader.languageSet));
  }
}
